import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isAxiosError } from 'axios';
import type { AiDraftSectionApi } from '@/components/LexicalEditor/plugins/AiDraftSectionPlugin';
import {
  APPROVAL_PROJECT_CONTRACT_DRAFT_URL,
  getProjectContractDraftTopic,
} from '@/constant/approval';
import { useSSE } from '@/hooks/sse/useSSE';
import { useDebugLogger } from '@/hooks/useDebugLogger';
import type {
  ProjectContractDraft,
  ProjectContractDraftEvent,
  ProjectContractDraftResponse,
} from '@/types/approval';
import { fetchApi } from '@/utils/api/customAxios';
import { buildAiDraftSectionInnerHtml } from '@/utils/approval/aiDraftSection';

/**
 * 계약별 임시 표기 상태. 화면 전용 — 저장 본문엔 절대 안 들어감.
 * - generating: POST 응답 대기 또는 pending → SSE 대기
 * - failed: 파이프라인 실패(failed[] / draft_error) → 재시도 가능
 * - request-failed: POST 자체 실패 → 재시도 가능
 * - source-unavailable: POST 424 — 법틀에서 계약서 원문을 못 받아 생성 시작 못 함 → 재시도 가능
 * - timeout: 워치독 재조회로도 결과 없음(SSE 유실 포함) → 재시도 가능
 * - inaccessible: 응답 drafts/pending/failed 어디에도 없음(접근 불가 계약) → 재시도 불가
 */
export type ProjectContractDraftStatusKind =
  | 'generating'
  | 'failed'
  | 'request-failed'
  | 'source-unavailable'
  | 'timeout'
  | 'inaccessible';

export type ProjectContractDraftStatus = {
  kind: ProjectContractDraftStatusKind;
  retryable: boolean;
};

type UseProjectContractDraftArgs = {
  enabled: boolean;
  passportId: string | null | undefined;
  /** 섹션 제목용 계약명 — 메타가 아직 없으면 fallback 을 돌려줄 것 */
  resolveTitle: (contractId: number) => string;
  editorApi: AiDraftSectionApi | null;
};

const DRAFT_READY = 'draft_ready';
const DRAFT_ERROR = 'draft_error';

/**
 * pending 이 남아 있을 때 같은 계약으로 재호출하면 완료분은 drafts 로 즉시 돌아오므로 재호출이
 * 곧 폴링. SSE 연결 실패·이벤트 유실을 이걸로 흡수하고, 한도 넘으면 timeout 으로 재시도 유도
 */
const WATCHDOG_INTERVAL_MS = 45_000;
const WATCHDOG_MAX_ROUNDS = 3;

const NON_RETRYABLE: ProjectContractDraftStatus = {
  kind: 'inaccessible',
  retryable: false,
};
const retryable = (
  kind: Exclude<ProjectContractDraftStatusKind, 'inaccessible'>,
): ProjectContractDraftStatus => ({ kind, retryable: true });

const readEventPayload = (
  data: ProjectContractDraftEvent,
): Partial<ProjectContractDraft> => {
  const payload = data?.payload ?? data;
  return payload as Partial<ProjectContractDraft>;
};

const toContractId = (value: unknown): number | null => {
  const id = Number(value);
  return Number.isFinite(id) && id > 0 ? id : null;
};

/**
 * 계약 품의(117·118) AI 초안 생성 — 등록된 계약별로 초안을 받아 본문 섹션으로 넣음.
 * 흐름: request → 임시 표기 generating → POST → drafts 즉시 반영 / pending 은 SSE
 * (project-contract-draft:{passportId}) draft_ready·draft_error 대기 + 워치독 재조회.
 * 에디터가 아직 안 떠 있으면 섹션 HTML 을 보류하고 API 등록 시점에 반영
 */
const useProjectContractDraft = ({
  enabled,
  passportId,
  resolveTitle,
  editorApi,
}: UseProjectContractDraftArgs) => {
  const logger = useDebugLogger();
  const loggerRef = useRef(logger);
  loggerRef.current = logger;
  const resolveTitleRef = useRef(resolveTitle);
  resolveTitleRef.current = resolveTitle;

  const [statuses, setStatuses] = useState<
    Record<number, ProjectContractDraftStatus>
  >({});
  const [pendingIds, setPendingIds] = useState<number[]>([]);
  // 삭제된 계약의 늦은 응답·이벤트가 다시 살아나지 않게 살아 있는 요청만 추적
  const activeIdsRef = useRef<Set<number>>(new Set());
  const deferredSectionsRef = useRef<Map<number, string>>(new Map());
  // 계약별 워치독 라운드 — 훅 전체로 세면 앞선 계약이 한도를 소진한 뒤 추가된 계약이 첫 틱에 timeout
  const watchdogRoundsRef = useRef<Map<number, number>>(new Map());

  const setStatus = useCallback(
    (contractId: number, status: ProjectContractDraftStatus | null) => {
      setStatuses((prev) => {
        if (!status) {
          if (!(contractId in prev)) return prev;
          const next = { ...prev };
          delete next[contractId];
          return next;
        }
        return { ...prev, [contractId]: status };
      });
    },
    [],
  );

  const removePending = useCallback((contractId: number) => {
    setPendingIds((prev) =>
      prev.includes(contractId) ? prev.filter((id) => id !== contractId) : prev,
    );
  }, []);

  const editorApiRef = useRef(editorApi);
  editorApiRef.current = editorApi;

  const applySection = useCallback((contractId: number, innerHtml: string) => {
    const api = editorApiRef.current;
    if (api) {
      api.upsert(contractId, innerHtml);
      return;
    }
    deferredSectionsRef.current.set(contractId, innerHtml);
  }, []);

  // 에디터가 늦게 mount 되면 보류분 반영
  useEffect(() => {
    if (!editorApi) return;
    deferredSectionsRef.current.forEach((innerHtml, contractId) => {
      editorApi.upsert(contractId, innerHtml);
    });
    deferredSectionsRef.current.clear();
  }, [editorApi]);

  const applyDraft = useCallback(
    (draft: Partial<ProjectContractDraft>) => {
      const contractId = toContractId(draft.contractId);
      if (!contractId || !activeIdsRef.current.has(contractId)) return;
      applySection(
        contractId,
        buildAiDraftSectionInnerHtml(
          resolveTitleRef.current(contractId),
          draft.content ?? '',
        ),
      );
      activeIdsRef.current.delete(contractId);
      watchdogRoundsRef.current.delete(contractId);
      setStatus(contractId, null);
      removePending(contractId);
    },
    [applySection, removePending, setStatus],
  );

  const failDraft = useCallback(
    (contractId: number, status: ProjectContractDraftStatus) => {
      if (!activeIdsRef.current.has(contractId)) return;
      watchdogRoundsRef.current.delete(contractId);
      setStatus(contractId, status);
      removePending(contractId);
    },
    [removePending, setStatus],
  );

  const request = useCallback(
    async (contractIds: number[]) => {
      const ids = contractIds.filter(
        (id, index, arr) => toContractId(id) && arr.indexOf(id) === index,
      );
      if (!enabled || ids.length === 0) return;

      ids.forEach((id) => {
        activeIdsRef.current.add(id);
        // 워치독 재조회로 들어온 계약은 라운드 유지, 새로 대기에 들어온 계약만 0
        if (!watchdogRoundsRef.current.has(id)) {
          watchdogRoundsRef.current.set(id, 0);
        }
        setStatus(id, retryable('generating'));
      });
      // 응답이 pending 이면 구독 중이어야 이벤트를 받으므로 POST 전에 토픽을 켬
      setPendingIds((prev) => Array.from(new Set([...prev, ...ids])));

      let data: ProjectContractDraftResponse | null = null;
      let failKind: 'request-failed' | 'source-unavailable' = 'request-failed';
      try {
        const res = await fetchApi<{ data?: ProjectContractDraftResponse }>({
          method: 'POST',
          url: APPROVAL_PROJECT_CONTRACT_DRAFT_URL,
          data: { contractIds: ids },
        });
        data = res?.data ?? null;
      } catch (error) {
        loggerRef.current.error('[ContractDraft] POST 실패:', error);
        if (isAxiosError(error) && error.response?.status === 424) {
          failKind = 'source-unavailable';
        }
      }
      if (!data) {
        ids.forEach((id) => failDraft(id, retryable(failKind)));
        return;
      }

      const handled = new Set<number>();
      (data.drafts ?? []).forEach((draft) => {
        const id = toContractId(draft.contractId);
        if (!id) return;
        handled.add(id);
        applyDraft(draft);
      });
      (data.failed ?? []).forEach((raw) => {
        const id = toContractId(raw);
        if (!id) return;
        handled.add(id);
        failDraft(id, retryable('failed'));
      });
      (data.pending ?? []).forEach((raw) => {
        const id = toContractId(raw);
        if (id) handled.add(id);
      });
      ids
        .filter((id) => !handled.has(id))
        .forEach((id) => failDraft(id, NON_RETRYABLE));
    },
    [applyDraft, enabled, failDraft, setStatus],
  );

  const retry = useCallback(
    (contractId: number) => {
      watchdogRoundsRef.current.delete(contractId);
      request([contractId]);
    },
    [request],
  );

  /** 계약 삭제 — 임시 표기·대기 상태를 버리고 본문 섹션도 제거 */
  const discard = useCallback(
    (contractId: number) => {
      activeIdsRef.current.delete(contractId);
      deferredSectionsRef.current.delete(contractId);
      watchdogRoundsRef.current.delete(contractId);
      setStatus(contractId, null);
      removePending(contractId);
      editorApiRef.current?.remove(contractId);
    },
    [removePending, setStatus],
  );

  // ----- SSE: pending 이 있을 때만 구독 -----
  const topics = useMemo(
    () =>
      enabled && passportId && pendingIds.length
        ? [getProjectContractDraftTopic(passportId)]
        : [],
    [enabled, passportId, pendingIds.length],
  );

  const onReady = useCallback(
    (data: ProjectContractDraftEvent) => {
      loggerRef.current.log('[ContractDraft] draft_ready', data);
      applyDraft(readEventPayload(data));
    },
    [applyDraft],
  );
  const onError = useCallback(
    (data: ProjectContractDraftEvent) => {
      loggerRef.current.warn('[ContractDraft] draft_error', data);
      const id = toContractId(readEventPayload(data).contractId);
      if (id) failDraft(id, retryable('failed'));
    },
    [failDraft],
  );

  const handlers = useMemo(
    () => ({ [DRAFT_READY]: onReady, [DRAFT_ERROR]: onError }),
    [onReady, onError],
  );

  useSSE(topics, handlers, topics.length > 0, {
    // data.type 이 이벤트명이 아닌 경우(토픽명 등) SSE event 필드로 재판정
    onUnhandledMessage: ({ data, rawEventName }) => {
      if (rawEventName === DRAFT_READY) onReady(data);
      else if (rawEventName === DRAFT_ERROR) onError(data);
    },
  });

  // ----- 워치독: pending 재조회, 한도 초과 시 timeout -----
  const requestRef = useRef(request);
  requestRef.current = request;
  const pendingIdsRef = useRef(pendingIds);
  pendingIdsRef.current = pendingIds;

  useEffect(() => {
    if (pendingIds.length === 0) {
      watchdogRoundsRef.current.clear();
      return;
    }
    const timer = window.setInterval(() => {
      const current = pendingIdsRef.current.filter((id) =>
        activeIdsRef.current.has(id),
      );
      if (current.length === 0) return;
      const rounds = watchdogRoundsRef.current;
      const expired: number[] = [];
      const requery: number[] = [];
      current.forEach((id) => {
        const round = (rounds.get(id) ?? 0) + 1;
        rounds.set(id, round);
        (round > WATCHDOG_MAX_ROUNDS ? expired : requery).push(id);
      });
      expired.forEach((id) => failDraft(id, retryable('timeout')));
      if (requery.length === 0) return;
      loggerRef.current.log('[ContractDraft] 워치독 재조회', requery);
      requestRef.current(requery);
    }, WATCHDOG_INTERVAL_MS);
    return () => window.clearInterval(timer);
    // 대기 목록이 비었다 다시 생길 때만 타이머를 다시 세움
  }, [pendingIds.length > 0, failDraft]);

  return { statuses, request, retry, discard };
};

export default useProjectContractDraft;
